import { useEffect, useState } from 'react';
// @types/react-pdf seems to be setting a different pdfjs-dist
// version as a dependency which screws up the react-pdf renderer.
// This is not the first time this has happened so
// we're uninstalling @types/react-pdf and ignoring the types here.
// @ts-ignore
import { Page, Document, pdfjs } from 'react-pdf';

import { observer } from 'mobx-react-lite';
import pdfWorker from 'pdfjs-dist/build/pdf.worker.min.js?url';

import { useWindowSize } from '@components/component-helpers';
import { RelativeSpinner } from '@components/loading-spinner';
import { Pagination } from '@components/pagination';
import { Stack } from '@components/stack';
import { getElementWidth } from '@utils/browser';

import { PreviewerProps } from './previewer';

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorker;

type PDFProps = {
  file: Blob | undefined;
} & Omit<PreviewerProps, 'url' | 'fallback'>;

export function PDF({ file, paginate = false, maxWidth, parentId }: PDFProps) {
  const { width } = useWindowSize();
  if (file === undefined || file === null) return null;

  return (
    <Render key={width} file={file} paginate={paginate} maxWidth={maxWidth} parentId={parentId} />
  );
}

function Render({ file, paginate = false, maxWidth, parentId }: PDFProps) {
  const [numPages, setNumPages] = useState(0);
  const [selectedPage, setSelectedPage] = useState(1);
  const [width, setWidth] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  let fileWidth: number | undefined;

  useEffect(() => {
    setWidth(getElementWidth(document.querySelector('#' + parentId)));
  }, [parentId]);

  if (maxWidth < width) {
    fileWidth = maxWidth;
  } else {
    fileWidth = width;
  }

  return (
    <>
      {isLoading && <RelativeSpinner />}
      <div>
        <Stack space={2}>
          {paginate && (
            <Pagination
              count={numPages}
              selected={selectedPage}
              onSelect={setSelectedPage}
              justify="right"
            />
          )}
          <Document
            file={file}
            loading={null}
            onLoadSuccess={({ numPages }: { numPages: number }) => {
              setNumPages(numPages);
              setIsLoading(false);
            }}
          >
            {paginate ? (
              <Page
                renderAnnotationLayer={false}
                renderTextLayer={false}
                pageNumber={selectedPage}
                width={fileWidth}
              />
            ) : (
              <Stack space={4}>
                {Array.from(new Array(numPages), (el, index) => (
                  <Page
                    key={`page_${index + 1}`}
                    renderAnnotationLayer={false}
                    renderTextLayer={false}
                    pageNumber={index + 1}
                    width={fileWidth}
                  />
                ))}
              </Stack>
            )}
          </Document>
          {paginate && (
            <Pagination
              count={numPages}
              selected={selectedPage}
              onSelect={setSelectedPage}
              justify="right"
            />
          )}
        </Stack>
      </div>
    </>
  );
}

export default observer(PDF);
